import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/Layout/AdminLayout';
import { getEmployeeProfile, saveOrUpdateEmployeeProfile, uploadFile } from '../api/api';

interface EmployeeProfile {
  id?: number;
  userId: number;
  name?: string;
  email?: string;
  username?: string;
  employeeId?: string;
  mobile: string;
  fullAddress: string;
  aadhaarNumber: string;
  panNumber: string;
  aadhaarImage: string;
  panImage: string;
  bankPassbook: string;
  accountNumber: string;
  ifscCode: string;
  joiningDate: string;
  jobDesignation: string;
  ctc: number;
  allowance: number;
  netSalary: number;
}

const Profile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const navigate = useNavigate();

  const [profile, setProfile] = useState<EmployeeProfile>({
    userId,
    mobile: '',
    fullAddress: '',
    aadhaarNumber: '',
    panNumber: '',
    aadhaarImage: '',
    panImage: '',
    bankPassbook: '',
    accountNumber: '',
    ifscCode: '',
    joiningDate: '',
    jobDesignation: '',
    ctc: 0,
    allowance: 0,
    netSalary: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // --- Inline Editing State ---
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isEmailEditing, setIsEmailEditing] = useState(false);
  // --------------------------

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getEmployeeProfile(userId);
        if (response.data) {
          setProfile({
            ...response.data,
            joiningDate: response.data.joiningDate ? new Date(response.data.joiningDate).toISOString().split('T')[0] : '',
          });
        }
      } catch (err) {
        console.error('Failed to fetch profile data, initializing a new profile form.');
        setError('No employee profile found. Please fill in the details to create one.');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: Number(value) }));
  };
  
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof EmployeeProfile) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    try {
      const response = await uploadFile(file);
      setProfile(prev => ({ ...prev, [fieldName]: response.data }));
      alert('File uploaded successfully!');
    } catch (err) {
      setError(`Failed to upload file for ${fieldName}.`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await saveOrUpdateEmployeeProfile(userId, {
        ...profile,
        ctc: Number(profile.ctc),
        allowance: Number(profile.allowance),
        netSalary: Number(profile.netSalary),
      });
      navigate('/admin/subordinates/manage');
    } catch (err) {
      setError('Failed to save profile. Please try again.');
    }
  };
  
  // --- Inline Save Handlers ---
  const handleNameSave = async () => {
    setError('');
    try {
      await saveOrUpdateEmployeeProfile(userId, {
        ...profile,
        name: profile.name,
      });
      setIsNameEditing(false);
    } catch (err) {
      setError('Failed to update name. Please try again.');
    }
  };

  const handleEmailSave = async () => {
    setError('');
    try {
      await saveOrUpdateEmployeeProfile(userId, {
        ...profile,
        email: profile.email,
      });
      setIsEmailEditing(false);
    } catch (err) {
      setError('Failed to update email. Please try again.');
    }
  };

  // Common Input Class for cleaner code
  const inputClass = "border border-gray-300 dark:border-gray-600 p-2 rounded-md w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors";
  const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";
  const sectionTitleClass = "text-xl font-semibold mb-4 text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2";

  if (loading) {
    return <AdminLayout><div className="text-center mt-20 text-gray-600 dark:text-gray-300">Loading profile...</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen text-gray-900 dark:text-white transition-colors duration-200">
        <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
          Employee Profile
        </h1>
        {error && <p className="text-red-500 dark:text-red-400 mb-4 bg-red-100 dark:bg-red-900/30 p-3 rounded">{error}</p>}
        
        {/* --- Non-Editable / Inline Editable Details --- */}
        <div className="mb-6 text-lg bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <p><strong className="text-gray-600 dark:text-gray-400">User ID:</strong> <span className="ml-2">{profile.id || userId}</span></p>
            <p><strong className="text-gray-600 dark:text-gray-400">Username:</strong> <span className="ml-2">{profile.username || 'Not Available'}</span></p>
            <p><strong className="text-gray-600 dark:text-gray-400">Employee ID:</strong> <span className="ml-2">{profile.employeeId || 'Not Available'}</span></p>
          </div>
          
          <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            {/* Name Field - Inline Editable */}
            <div className="flex items-center">
              <strong className="text-gray-600 dark:text-gray-400 w-24">Name:</strong>
              {isNameEditing ? (
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    name="name" 
                    value={profile.name || ''} 
                    onChange={handleChange} 
                    className="border border-gray-400 dark:border-gray-600 p-1 rounded text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                  />
                  <button type="button" onClick={handleNameSave} className="text-green-600 dark:text-green-400 hover:text-green-800 text-sm font-bold">Save</button>
                  <button type="button" onClick={() => setIsNameEditing(false)} className="text-red-600 dark:text-red-400 hover:text-red-800 text-sm font-bold">Cancel</button>
                </div>
              ) : (
                <span onClick={() => setIsNameEditing(true)} className="cursor-pointer text-blue-600 dark:text-blue-400 hover:underline">
                  {profile.name || 'Click to Add Name'}
                </span>
              )}
            </div>

            {/* Email Field - Inline Editable */}
            <div className="flex items-center">
              <strong className="text-gray-600 dark:text-gray-400 w-24">Email:</strong>
              {isEmailEditing ? (
                <div className="flex items-center gap-2">
                  <input 
                    type="email" 
                    name="email" 
                    value={profile.email || ''} 
                    onChange={handleChange} 
                    className="border border-gray-400 dark:border-gray-600 p-1 rounded text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
                  />
                  <button type="button" onClick={handleEmailSave} className="text-green-600 dark:text-green-400 hover:text-green-800 text-sm font-bold">Save</button>
                  <button type="button" onClick={() => setIsEmailEditing(false)} className="text-red-600 dark:text-red-400 hover:text-red-800 text-sm font-bold">Cancel</button>
                </div>
              ) : (
                <span onClick={() => setIsEmailEditing(true)} className="cursor-pointer text-blue-600 dark:text-blue-400 hover:underline">
                  {profile.email || 'Click to Add Email'}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* --- Main Form --- */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          
          {/* Personal Details */}
          <div className="mb-8">
            <h2 className={sectionTitleClass}>Personal Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                 <label className={labelClass}>Mobile Number</label>
                 <input type="text" name="mobile" value={profile.mobile} onChange={handleChange} placeholder="Enter Mobile" className={inputClass} required />
              </div>
              <div>
                 <label className={labelClass}>Full Address</label>
                 <input type="text" name="fullAddress" value={profile.fullAddress} onChange={handleChange} placeholder="Enter Address" className={inputClass} required />
              </div>
              <div>
                 <label className={labelClass}>Aadhaar Number</label>
                 <input type="text" name="aadhaarNumber" value={profile.aadhaarNumber} onChange={handleChange} placeholder="Enter Aadhaar" className={inputClass} required />
              </div>
              <div>
                 <label className={labelClass}>PAN Number</label>
                 <input type="text" name="panNumber" value={profile.panNumber} onChange={handleChange} placeholder="Enter PAN" className={inputClass} required />
              </div>
              
              {/* File Uploads */}
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded border border-gray-200 dark:border-gray-600">
                <label htmlFor="aadhaarImage" className={labelClass}>Aadhaar Image</label>
                <input type="file" id="aadhaarImage" onChange={(e) => handleFileUpload(e, 'aadhaarImage')} className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900 dark:file:text-blue-300 hover:file:bg-blue-100" />
                {profile.aadhaarImage && <a href={`https://ayushvatika.com${profile.aadhaarImage}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 text-sm mt-2 inline-block hover:underline">View Uploaded Aadhaar</a>}
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded border border-gray-200 dark:border-gray-600">
                <label htmlFor="panImage" className={labelClass}>PAN Image</label>
                <input type="file" id="panImage" onChange={(e) => handleFileUpload(e, 'panImage')} className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900 dark:file:text-blue-300 hover:file:bg-blue-100" />
                {profile.panImage && <a href={`https://ayushvatika.com${profile.panImage}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 text-sm mt-2 inline-block hover:underline">View Uploaded PAN</a>}
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="mb-8">
            <h2 className={sectionTitleClass}>Bank Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                 <label className={labelClass}>Account Number</label>
                 <input type="text" name="accountNumber" value={profile.accountNumber} onChange={handleChange} placeholder="Enter Account No" className={inputClass} required />
              </div>
              <div>
                 <label className={labelClass}>IFSC Code</label>
                 <input type="text" name="ifscCode" value={profile.ifscCode} onChange={handleChange} placeholder="Enter IFSC" className={inputClass} required />
              </div>
              <div className="md:col-span-2 bg-gray-50 dark:bg-gray-700/50 p-4 rounded border border-gray-200 dark:border-gray-600">
                <label htmlFor="bankPassbook" className={labelClass}>Bank Passbook/Cheque Image</label>
                <input type="file" id="bankPassbook" onChange={(e) => handleFileUpload(e, 'bankPassbook')} className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900 dark:file:text-blue-300 hover:file:bg-blue-100" />
                {profile.bankPassbook && <a href={`https://ayushvatika.com${profile.bankPassbook}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 text-sm mt-2 inline-block hover:underline">View Uploaded Passbook</a>}
              </div>
            </div>
          </div>

          {/* Job Profile */}
          <div className="mb-8">
            <h2 className={sectionTitleClass}>Job Profile</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                 <label className={labelClass}>Joining Date</label>
                 <input type="date" name="joiningDate" value={profile.joiningDate} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                 <label className={labelClass}>Job Designation</label>
                 <input type="text" name="jobDesignation" value={profile.jobDesignation} onChange={handleChange} placeholder="Designation" className={inputClass} required />
              </div>
              <div>
                 <label className={labelClass}>CTC</label>
                 <input type="number" name="ctc" value={profile.ctc} onChange={handleNumericChange} placeholder="0" className={inputClass} required />
              </div>
              <div>
                 <label className={labelClass}>Allowance</label>
                 <input type="number" name="allowance" value={profile.allowance} onChange={handleNumericChange} placeholder="0" className={inputClass} required />
              </div>
              <div>
                 <label className={labelClass}>Net Salary</label>
                 <input type="number" name="netSalary" value={profile.netSalary} onChange={handleNumericChange} placeholder="0" className={inputClass} required />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="submit" className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 shadow-lg font-semibold transition-all">
                {profile.employeeId ? 'Update Profile' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default Profile;